'use client';

import { createContext, ReactNode, useContext, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import useSWR, { type KeyedMutator } from 'swr';

import { apiRoutes, getApiUrl } from '@/constants/api';
import { AUTH_PATHS } from '@/constants/routes';
import { clientApiFetcher } from '@/lib/api/fetcher/client';
import type { GetMeResponse, User } from '@/types/auth';

type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | undefined;
  user: User | undefined;
  mutate: KeyedMutator<GetMeResponse>;
  getGoogleAuthUrl: (params?: {
    callbackUrl?: string;
    action?: string;
    ref?: string;
  }) => string;
  getLinkedinAuthUrl: (params?: {
    callbackUrl?: string;
    action?: string;
    ref?: string;
  }) => string;
  logout: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const pathname = usePathname();
  const router = useRouter();

  const { data, isLoading, error, mutate } = useSWR<GetMeResponse>(
    getApiUrl(apiRoutes.auth.me),
    clientApiFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      shouldRetryOnError: false,
      dedupingInterval: 5000,
      refreshInterval: 0,
      revalidateIfStale: false,
      suspense: false,
      keepPreviousData: true,
      errorRetryCount: 0,
    },
  );

  // Handle authentication state changes and redirects
  useEffect(() => {
    const isAuthenticated = data?.user !== undefined;
    const isAuthPath =
      pathname !== '/auth/signup/verify-email' &&
      AUTH_PATHS.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`),
      );

    // If we're on an auth path and become authenticated, handle redirect
    if (isAuthPath && isAuthenticated) {
      if (data?.user && !data.user.isVerified) {
        router.push('/auth/signup/verify-email');
        return;
      }

      if (data?.user && !data.user.isOnboarded) {
        router.push('/onboarding');
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      const callbackUrl = searchParams.get('callbackUrl');

      if (callbackUrl) {
        const decodedCallback = decodeURIComponent(callbackUrl);
        if (
          decodedCallback.startsWith('/') &&
          !AUTH_PATHS.some((path) => decodedCallback.startsWith(path))
        ) {
          router.push(decodedCallback);
        } else {
          router.push('/feed');
        }
      } else {
        router.push('/feed');
      }
    }
  }, [data?.user, router, pathname]);

  // Ensure we check auth state when pathname changes
  useEffect(() => {
    if (
      pathname !== '/auth/signup/verify-email' &&
      AUTH_PATHS.some(
        (path) => pathname === path || pathname.startsWith(`${path}/`),
      )
    ) {
      mutate();
    }
  }, [pathname, mutate]);

  const logout = async () => {
    try {
      await clientApiFetcher(getApiUrl(apiRoutes.auth.logout), {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear the cache and force a page reload to ensure clean state
      mutate(undefined, false);
      window.location.href = '/';
    }
  };

  type GetGoogleParams = {
    callbackUrl?: string;
    action?: string;
    ref?: string;
  };

  type GetLinkedinParams = {
    callbackUrl?: string;
    action?: string;
    ref?: string;
  };

  const getGoogleAuthUrl = (params?: GetGoogleParams) => {
    const url = new URL(getApiUrl(apiRoutes.auth.googleAuth));
    if (params?.callbackUrl)
      url.searchParams.set('callbackUrl', params.callbackUrl);
    if (params?.action) url.searchParams.set('action', params.action);
    if (params?.ref) url.searchParams.set('ref', params.ref);
    return url.toString();
  };

  const getLinkedinAuthUrl = (params?: GetLinkedinParams) => {
    const url = new URL(getApiUrl(apiRoutes.auth.linkedinAuth));
    if (params?.callbackUrl)
      url.searchParams.set('callbackUrl', params.callbackUrl);
    if (params?.action) url.searchParams.set('action', params.action);
    if (params?.ref) url.searchParams.set('ref', params.ref);
    return url.toString();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: data?.user !== undefined,
        user: data?.user,
        isLoading,
        error,
        mutate,
        getGoogleAuthUrl,
        getLinkedinAuthUrl,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
